import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../config/firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import './ShopperLogin.css';

function ShopperLogin() {
  const [isLogin, setIsLogin] = useState(true);
  const [credentials, setCredentials] = useState({ email: '', password: '', name: '' });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginShopper } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else {
      setCredentials({ ...credentials, [name]: value });
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Sign in with Firebase
        if (!credentials.email || !credentials.password) {
          setError('Please fill in all fields');
          setLoading(false);
          return;
        }

        if (!validateEmail(credentials.email)) {
          setError('Please enter a valid email address');
          setLoading(false);
          return;
        }

        const userCredential = await signInWithEmailAndPassword(
          auth,
          credentials.email,
          credentials.password
        );

        const idToken = await userCredential.user.getIdToken();
        
        // Login with our backend using Firebase token
        const result = await loginShopper(idToken, {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          name: userCredential.user.displayName || userCredential.user.email.split('@')[0]
        });

        if (result.success) {
          navigate('/shopper/account');
        } else {
          // If backend fails but Firebase succeeded, sign out
          await signOut(auth);
          setError(result.message || 'Login failed. Please try again.');
        }
      } else {
        // Register with Firebase
        if (!credentials.email || !credentials.password || !credentials.name) {
          setError('Please fill in all fields');
          setLoading(false);
          return;
        }

        if (!validateEmail(credentials.email)) {
          setError('Please enter a valid email address');
          setLoading(false);
          return;
        }

        if (credentials.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        if (credentials.password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        // Create Firebase user
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          credentials.email,
          credentials.password
        );

        const idToken = await userCredential.user.getIdToken();

        // Register with our backend
        const result = await loginShopper(idToken, {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          name: credentials.name,
          isNewUser: true
        });

        if (result.success) {
          navigate('/shopper/account');
        } else {
          // If backend fails, delete Firebase user
          await userCredential.user.delete();
          setError(result.message || 'Registration failed. Please try again.');
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      
      // Handle specific Firebase errors
      if (err.code === 'auth/user-not-found') {
        setError('Email not found. Please register or check your email.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/email-already-in-use') {
        setError('Email already registered. Please login instead.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError(err.message || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="shopper-login-container">
      <div className="login-card">
        <h1>{isLogin ? 'Welcome Back' : 'Create Account'}</h1>
        <p className="subtitle">
          {isLogin 
            ? 'Sign in to track your orders and save favorites' 
            : 'Create an account to shop and track your orders'}
        </p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                name="name"
                value={credentials.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Email Address</label>
            <input
              type="email"
              name="email"
              value={credentials.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
            />
            {!isLogin && <small>Minimum 6 characters</small>}
          </div>

          {!isLogin && (
            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
              />
            </div>
          )}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading 
              ? (isLogin ? 'Signing in...' : 'Creating account...') 
              : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <div className="toggle-auth">
          <p>
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setCredentials({ email: '', password: '', name: '' });
                setConfirmPassword('');
              }}
              className="toggle-link"
            >
              {isLogin ? 'Register' : 'Sign In'}
            </button>
          </p>
        </div>

        <div className="guest-checkout">
          <p>Or <Link to="/checkout">continue as guest</Link></p>
        </div>
      </div>
    </div>
  );
}

export default ShopperLogin;
